import React, { useReducer, useState } from "react";
import PolicyTypeTab from "./tabs/PolicyTypeTab";
import ResourceSelectionTab from "./tabs/ResourceSelectionTab";
import ValidationTab from "./tabs/ValidationTab";
import ReviewPushTab from "./tabs/ReviewPushTab";
import FinalPhaseTab from "./tabs/FinalPhaseTab";
import {
  fetchExistingPolicy,
  generatePolicy,
  modifyPolicy,
  validatePolicy,
  pushPolicyToGit,
  downloadPolicy
} from "../services/multicloudAPI";

interface State {
  fileName: string;
  modification_type: any;
  policyName: string;
  description: string;
  enforceLevel: string;
  provider: string;
  policyCode: string; // Add policyCode to state
  commit_message: string;
  repo_url: string;
  branch: string;
  file_path: string;
  directory_path: string;
  modification_instructions: string;
  category: string;
}

type Action =
  | { type: "SET_FIELD"; field: keyof State; value: any }
  | { type: "RESET_FIELD" };
// --- initial state and reducer ---
const initialState: State = {
  policyName: "my_policy",
  description: "",
  enforceLevel: "hard-mandatory",
  provider: "",
  modification_type: "modify",
  fileName: "",
  policyCode: "",
  commit_message: "",
  repo_url: "",
  branch: "",
  file_path: "",
  directory_path: "",
  modification_instructions: "",
  category: ""
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "SET_FIELD":
      return { ...state, [action.field]: action.value } as State;
       case "RESET_FIELD":
      return { ...state, ...initialState } as State;
    default:
      return state;
  }
}


export default function MultiCloudAccelerator() {
   const [state, dispatch] = useReducer(reducer, initialState);
    const [activeTab, setActiveTab] = useState<string>("Policy Type");
    const[generatedPlolicy,setGeneratedPolicy]=useState({});
    const [showProgress,setShowProgress]=useState<boolean>(false);
    const [showReGenerate,setShowRegenerate]=useState<boolean>(false);
    type PolicyMode = 'existing' | 'new' | undefined;
  
    const [policyMode, setPolicyMode] = useState<PolicyMode>(undefined);
  const [showRepos,setShowRepos]=useState<boolean>(false);
    // State to control Retest button visibility
    const [showRetest, setShowRetest] = useState<boolean>(false);
  
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState<"success" | "error">("success");
    const [tab, setTab] = useState("push");
    const [testResult, setTestResult] = useState<any>(null);
  const [isRegenerating, setIsRegenerating] = useState<boolean>(false);
  const [progressMessage, setProgressMessage] = useState<string>("");
  const showMessage = (text: string, type: "success" | "error") => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage("");
    }, 30000);
  };
  
    // API call for existing policy
    const handleExistingPolicySelect = async (repoURL: string, branch: string, filePath: string) => {
      try {
        setProgressMessage("Fetching existing policy...");
        setShowProgress(true);
        
        // Store git details in state
        dispatch({ type: "SET_FIELD", field: "repo_url", value: repoURL || state.repo_url });
        dispatch({ type: "SET_FIELD", field: "branch", value: branch || state.branch });
        dispatch({ type: "SET_FIELD", field: "file_path", value: filePath || state.file_path });
        
        const data = await fetchExistingPolicy({
          repo_url: repoURL || state.repo_url,
          branch: branch || state.branch,
          file_path: filePath || state.file_path
        });
        
        if (data.success) {
          // Update state with fetched policy details
          dispatch({ type: "SET_FIELD", field: "description", value: data.policy_code });
          dispatch({ type: "SET_FIELD", field: "policyCode", value: data.policy_code });
          if (data.policy_name) dispatch({ type: "SET_FIELD", field: "policyName", value: data.policy_name });
          if (data.cloud_provider) dispatch({ type: "SET_FIELD", field: "provider", value: data.cloud_provider });
          if (data.enforcement_level) dispatch({ type: "SET_FIELD", field: "enforceLevel", value: data.enforcement_level });
          if (data.policy_category) dispatch({ type: "SET_FIELD", field: "category", value: data.policy_category });
          
          showMessage("Existing policy loaded successfully", "success");
          return data.policy_code;
        } else {
          showMessage(data.error_message || "Failed to fetch policy", "error");
          return null;
        }
      } catch (error) {
        console.error("Error fetching policy:", error);
        showMessage("Failed to fetch existing policy", "error");
        return null;
      } finally {
        setShowProgress(false);
      }
    };
  
    const tabs = ["Policy Type","Resource Selection", "Run Validation", "Review & Push"];
    const labels = ["Start manage Policy", "Define Policy Requirement", "Run Validation", "Review & Push"];
  
      async function handleGenerate(): Promise<void> {
        try {
          // Prepare payload
          if(state.description===""||!state.provider) 
            {
               showMessage("Policy description and cloud platform is required","error");
               return;
            }
              setProgressMessage("Generating policy...");
          setShowProgress(true);
          const payload = {
            requirement: state.description,
            cloud_provider: state.provider,
            validate: true
          };
          const data = await generatePolicy(payload);
          if (data) {
            setGeneratedPolicy(data);
            if (data.description) dispatch({ type: "SET_FIELD", field: "description", value: data.description });
            if (data.policy_name) dispatch({ type: "SET_FIELD", field: "policyName", value: data.policy_name });
            if (data.enforcement_level) dispatch({ type: "SET_FIELD", field: "enforceLevel", value: data.enforcement_level });
            if (data.cloud_provider) dispatch({ type: "SET_FIELD", field: "provider", value: data.cloud_provider });
            if (data.policy_code) dispatch({ type: "SET_FIELD", field: "policyCode", value: data.policy_code });
             if (data.policy_category) dispatch({ type: "SET_FIELD", field: "category", value: data.policy_category });
             setActiveTab("Run Validation");
               showMessage("Policy generated successfully.", "success");
          }
        } catch (error) {
          console.error("Error generating policy:", error);
          showMessage("Failed to generate policy","error");
        }
        finally{
          setShowProgress(false);
        }
      }
    async function handleModify(): Promise<void> {
      try {
        // Validate required fields
        if (state.modification_instructions === "" || !state.provider) {
          showMessage("Modification instructions and provider are required", "error");
          return;
        }
        setProgressMessage("Modifying policy...");
        setShowProgress(true);
        setIsRegenerating(false);
        
        // Prepare payload - use remediation_prompt if regenerating, otherwise use description
        const payload = {
          existing_policy: state.description || state.policyCode,
          modification_instructions: state.modification_instructions,
          cloud_provider: state.provider,
          modification_type: state.modification_type || "modify",
          validate: true
        };

        const data = await modifyPolicy(payload);
        
        if (data && data.policy_code) {
          // Update state with modified policy
          dispatch({ type: "SET_FIELD", field: "policyCode", value: data.policy_code });
          dispatch({ type: "SET_FIELD", field: "description", value: data.policy_code });
          
          if (data.policy_name) dispatch({ type: "SET_FIELD", field: "policyName", value: data.policy_name });
          if (data.enforcement_level) dispatch({ type: "SET_FIELD", field: "enforceLevel", value: data.enforcement_level });
          if (data.cloud_provider) dispatch({ type: "SET_FIELD", field: "provider", value: data.cloud_provider });
          if (data.policy_category) dispatch({ type: "SET_FIELD", field: "category", value: data.policy_category });
          
          setGeneratedPolicy(data);
          setTestResult(null);
          setShowRegenerate(false);
          setShowRetest(false);
          
          showMessage("Policy modified successfully", "success");
          setActiveTab("Run Validation");
        } else {
          showMessage("Failed to modify policy", "error");
        }
      } catch (error) {
        console.error("Error modifying policy:", error);
        showMessage("Failed to modify policy", "error");
      } finally {
        setShowProgress(false);
      }
    }
    async function validatePolicyHandler() {
      try {
          setProgressMessage("Validating policy...");
        setShowProgress(true);
        
        const validationPayload = {
          policy_code: state.policyCode,
          policy_name: state.policyName,
          policy_category: state.category || "",
          cloud_provider: state.provider,
          fetch_from_rag: true,
          max_retries: 5
        };

        const validationResult = await validatePolicy(validationPayload);
        setTestResult(validationResult);
        
        // Check if validation passed (success_rate >= 75%)
        if (validationResult.success_rate >= 75.0) {
          setShowRegenerate(false);
          setShowRetest(false);
          setActiveTab("Review & Push");
         showMessage(`Policy validated successfully with ${validationResult.success_rate}% success rate ( 75% is the threshold value to push policy)`, "success");
        } else {
          // Validation failed - enable regeneration
          setShowRegenerate(true);
          setShowRetest(true);
          showMessage("Policy validation failed( 75% is the threshold value to push policy). Please modify and regenerate", "error");
        }
      } catch (error) {
        console.error("Error validating policy:", error);
        showMessage("Failed to validate policy", "error");
      } finally {
        setShowProgress(false);
      }
    }
      async function pushToGit() {
      try {
        if (state.fileName === "" || state.commit_message === "") {
          showMessage("File Name and commit message are required", "error");
          return;
        }
        
        setShowProgress(true);
        
        const pushPayload = {
          policy_code: state.policyCode,
          File_name: state.fileName,
          cloud_provider: state.provider,
          commit_message: state.commit_message
        };
        
        const pushResult = await pushPolicyToGit(pushPayload);
        
        if (pushResult.push_success) {
          showMessage("Policy pushed to Git successfully!", "success");
          
          // Reset state and navigate to final phase
          setActiveTab("Final Phase");
          dispatch({ type: "RESET_FIELD" });
          setPolicyMode(undefined);
          setGeneratedPolicy({});
          setTestResult(null);
          setShowRegenerate(false);
          setShowRetest(false);
          setIsRegenerating(false);
        } else {
          showMessage(pushResult.error_details || "Failed to push policy", "error");
        }
      } catch (error) {
        console.error("Error pushing policy:", error);
        showMessage("Failed to push policy", "error");
      } finally {
        setShowProgress(false);
      }
    }

  async function downloadPolicyHandler() {
    try {
      const blob = await downloadPolicy({
        policy_code: state.policyCode,
        policy_name: state.policyName
      });
      
      dispatch({ type: "RESET_FIELD" });
      setPolicyMode(undefined);
      setGeneratedPolicy({});
      setMessage("");  
      setActiveTab("Final Phase");
      setTestResult(null);
      setTab("download");
      
      // Create a link element to trigger the download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = state.policyName || "policy.sentinel";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      showMessage("Policy downloaded successfully", "success");
    } catch (error) {
      console.error("Error downloading policy:", error);
      showMessage("Failed to download policy", "error");
    }
  }



const handlePolicyTypeTab = async () => {
  if(policyMode)  
    setActiveTab("Resource Selection");
  else
    showMessage("Please select policy mode","error");
}

const handleGoHome = () => {
  setActiveTab("Policy Type");
}
const downloadSentinelFile = () => {
  const blob = new Blob([state.policyCode], {
    type: "sentinel",
  });

  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = state.policyName || "policy.sentinel";
  a.click();

  URL.revokeObjectURL(url);
};

  return (
    <div className="flex h-full w-full" style={{ color: '#2B2733' }}>
      {/* Sidebar */}
      <aside className="w-64 bg-slate-50 border-r border-slate-200 px-4 py-5 overflow-y-auto">
        <h3 className="mb-6 text-lg font-semibold text-blue-600">MultiCloud Policy Accelerator</h3>

        <ul className="space-y-3">
          {tabs.map((item,index) => (
            <li
              key={item}
                
              className={`flex items-center gap-6 cursor-pointer transition-colors py-3
                ${activeTab === item
                  ? "text-blue-700 font-medium"
                  : "text-slate-600 hover:text-slate-800"}`}
            >
              <span
                className={`w-2.5 h-2.5 rounded-full 
                  ${activeTab=== item ? "bg-blue-600" : "bg-slate-400"}`}
              ></span>
              {labels[index]}
            </li>
          ))}
        </ul>
      </aside>

      {/* Content */}
      <main className="flex-1 bg-white overflow-y-auto relative">
         {/* Right Panel (tab content) */}
       <div className="flex flex-col justify-start px-6 py-5" style={{width:"fit-content"}}>
        {showProgress && (
  <div className="absolute inset-0 bg-white/75 backdrop-blur-[2px] flex flex-col items-center justify-center z-50 gap-3">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-dashed border-blue-600"></div>
    {progressMessage && (
      <div className="flex items-center gap-1.5">
        <p className="text-sm font-medium text-blue-700">{progressMessage}</p>
        <span className="flex gap-0.5 items-end pb-0.5">
          <span className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
          <span className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
          <span className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
        </span>
      </div>
    )}
  </div>
)}

    <div className="w-full">
        {activeTab === "Policy Type" && (
          <PolicyTypeTab
            policyMode={policyMode}
            setPolicyMode={setPolicyMode}
            handleExistingPolicySelect={handleExistingPolicySelect}
            onProceed={handlePolicyTypeTab}
          />
        )}

        {activeTab === "Resource Selection" && (
          <ResourceSelectionTab
            state={{
              description: state.description,
              modification_instructions: state.modification_instructions,
              provider: state.provider,
              modification_type: state.modification_type
            }}
            policyMode={policyMode}
            showRegenerate={showReGenerate}
            showProgress={showProgress}
            onDescriptionChange={(value) => dispatch({ type: "SET_FIELD", field: "description", value })}
            onModificationInstructionsChange={(value) => dispatch({ type: "SET_FIELD", field: "modification_instructions", value })}
            onProviderChange={(value) => dispatch({ type: "SET_FIELD", field: "provider", value })}
            onModificationTypeChange={(value) => dispatch({ type: "SET_FIELD", field: "modification_type", value })}
            onGenerate={handleGenerate}
            onModify={handleModify}
          />
        )}

        {activeTab === "Run Validation" && (
          <ValidationTab
            policyCode={state.policyCode}
            testResult={testResult}
            showProgress={showProgress}
            showRetest={showRetest}
            onValidate={validatePolicyHandler}
            onDownload={downloadSentinelFile}
            onRegenerate={() => {
              setIsRegenerating(true);
              setPolicyMode("existing");
              setActiveTab("Resource Selection");
              setTestResult(null);
            }}
            onCancel={() => {
             dispatch({ type: "RESET_FIELD" });
      setPolicyMode(undefined);
      setGeneratedPolicy({});
      setMessage("");  
      setTestResult(null);
      handleGoHome();
            }
          }
          />
        )}

        {activeTab === "Review & Push" && (
          <ReviewPushTab
            policyCode={state.policyCode}
            state={{
              fileName: state.fileName,
              commit_message: state.commit_message,
              provider: state.provider
            }}
            testResult={testResult}
            showRetest={showRetest}
            showProgress={showProgress}
            onFileNameChange={(value) => dispatch({ type: "SET_FIELD", field: "fileName", value })}
            onCommitMessageChange={(value) => {
              dispatch({ type: "SET_FIELD", field: "commit_message", value });
              if(generatedPlolicy) {
                generatedPlolicy["commit_message"] = value;
                setGeneratedPolicy(generatedPlolicy);
              }
            }}
            onPushToGit={pushToGit}
            onDownload={downloadPolicyHandler}
          />
        )}

        {activeTab === "Final Phase" && (
          <FinalPhaseTab
            downloadMode={tab === "download"}
            onHome={handleGoHome}
          />
        )}
        
       {message && (
  <div
   className={`px-3 py-2 text-sm font-medium
      ${messageType === "success" ? "text-green-600" : "text-red-600"}
    `}
  >
    {message}
  </div>
)}
        <div>
          


    </div>
      </div>
      </div>
      </main>
    </div>
  );
}